/* AUTO-EXTRACTED: REEDOS Premium Proposal print stylesheet.
   Single source of truth shared by the production ProposalPrint page
   and the Proposal Builder Prototype commercials preview.
   Extracted from /app/frontend/src/pages/ProposalPrint.jsx Iter 97.7. */
export const REEDOS_PRINT_STYLES = `
:root {
  --reedos-blue: #1A237E;
  --reedos-orange: #E8472C;
  --ink: #111827;
  --muted: #4B5563;
  --light: #6B7280;
  --rule: #E5E7EB;
  --rule-strong: #D1D5DB;
  --table-header: #F8FAFC;
  --card-bg: #FAFAFA;
}

* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

@page {
  size: A4;
  margin: 16mm 14mm 24mm 14mm;
  @bottom-left {
    content: "REEDOS Software Solutions Private Limited  ·  GSTIN 27AAJCR1173P1ZT";
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 7pt;
    color: #6B7280;
    letter-spacing: 0.02em;
  }
  @bottom-right {
    content: "Page " counter(page) " of " counter(pages);
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 7pt;
    color: #6B7280;
    letter-spacing: 0.06em;
  }
}

@media print {
  /* HIDE app shell on print */
  [data-testid="app-layout"] > aside,
  [data-testid="app-header"],
  [data-testid="app-sidebar"],
  aside,
  nav[aria-label="Sidebar"],
  /* 2026-02-10 REEDOS branding compliance: removed legacy
     third-party-badge hide-rules; the loader that injected those
     elements no longer ships in any build. */
  [data-sonner-toaster],
  [cmdk-overlay],
  [cmdk-root],
  .no-print { display: none !important; }

  body, html { background: white !important; margin: 0 !important; padding: 0 !important; }
  [data-testid="app-layout"] { display: block !important; }
  [data-testid="app-layout"] > main { display: block !important; }

  /* Strip on-screen card framing in print */
  .print-shell {
    box-shadow: none !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
    max-width: none !important;
    width: 100% !important;
    background: white !important;
  }

  /* Page-break management */
  h1, h2 { page-break-after: avoid; break-after: avoid; }
  table, .signature-block, .commercials-card { page-break-inside: avoid; break-inside: avoid; }
  .page-break-before { page-break-before: always; break-before: page; }
}

/* ── Container ─────────────────────────────────────────────── */
.print-shell {
  max-width: 820px;
  margin: 24px auto;
  background: white;
  padding: 18mm 16mm 14mm 16mm;
  border: 1px solid var(--rule);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--ink);
  font-size: 10pt;
  line-height: 1.55;
  font-feature-settings: 'ss01', 'cv11';
}
.print-shell h1, .print-shell h2, .print-shell h3 {
  font-family: 'Satoshi', 'Inter', sans-serif;
  color: var(--ink);
  margin: 0;
}

/* ── Top header bar (logo + meta) ─────────────────────────── */
.print-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 8mm;
  border-bottom: 1px solid var(--rule);
  margin-bottom: 10mm;
}
.print-header .logo { height: 38px; width: auto; display: block; }
.print-header .doc-meta {
  text-align: right;
  font-size: 8pt;
  color: var(--light);
  line-height: 1.6;
  letter-spacing: 0;
}
.print-header .doc-meta .tag {
  display: inline-block;
  padding: 2px 8px;
  background: var(--reedos-blue);
  color: #fff;
  font-size: 7pt;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  border-radius: 2px;
  margin-bottom: 4px;
}
.print-header .doc-meta strong { color: var(--ink); font-weight: 700; }
.print-header .doc-meta .num { font-family: 'Satoshi', sans-serif; font-size: 9pt; color: var(--ink); font-weight: 600; }

/* ── Eyebrow labels ───────────────────────────────────────── */
.eyebrow {
  font-family: 'Inter', sans-serif;
  font-size: 8pt;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--reedos-orange);
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.eyebrow .num {
  font-family: 'Satoshi', sans-serif;
  color: var(--reedos-blue);
  font-weight: 700;
  font-size: 9pt;
  letter-spacing: 0.04em;
}
.eyebrow .num::after { content: " /"; color: var(--rule-strong); margin-left: 2px; font-weight: 400; }

/* ── Section title group (eyebrow + H2 + accent rule) ─────── */
/* 2026-07-02 · V1 acceptance defect fix — break-after:avoid on the
   section-title wrapper prevents an orphaned heading on the previous
   page when the following card (e.g. .commercials-card) is forced
   onto a new page by its own break-inside:avoid. Without this, the
   heading block would render alone on page N while the table lands on
   page N+1. Applies to all four sections (Scope · Commercials ·
   Payment Terms · T&C). Legacy fallback page-break-after:avoid
   included for print engines that don't yet honour the modern spec. */
.section-title {
  margin-bottom: 6mm;
  break-after: avoid;
  page-break-after: avoid;
  break-inside: avoid;
  page-break-inside: avoid;
}
.section-title h2 {
  font-size: 14pt;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.005em;
  margin-top: 2mm;
}
.section-title .accent-rule {
  width: 22mm;
  height: 2px;
  background: var(--reedos-orange);
  margin-top: 4mm;
}
.section { margin-bottom: 14mm; }

/* ── Cover page ──────────────────────────────────────────── */
.cover {
  display: flex;
  flex-direction: column;
  position: relative;
  padding-top: 0;
  page-break-after: always;
  break-after: page;
}
.cover .cover-hero { padding-top: 14mm; flex: 1; }
.cover .cover-prepared { font-size: 8pt; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: var(--reedos-orange); margin-bottom: 5mm; }
.cover .cover-client {
  font-family: 'Satoshi', sans-serif;
  font-size: 24pt;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: var(--ink);
  max-width: 165mm;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.cover .cover-rule {
  width: 30mm;
  height: 3px;
  background: var(--reedos-blue);
  margin-top: 6mm;
  margin-bottom: 9mm;
}
.cover .cover-tag {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 3mm 5mm;
  background: var(--ink);
  color: #fff;
  font-family: 'Satoshi', sans-serif;
  font-size: 9pt;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.cover .cover-tag .pipe { width: 1px; height: 12px; background: rgba(255,255,255,0.3); }
.cover .meta-card {
  margin-top: 10mm;
  border-top: 2px solid var(--reedos-blue);
  border-bottom: 1px solid var(--reedos-blue);
}
.cover .meta-card .row {
  display: grid;
  grid-template-columns: 38mm 1fr;
  padding: 2.4mm 0;
  border-bottom: 1px solid var(--rule);
  font-size: 9.5pt;
  align-items: start;
}
.cover .meta-card .row:last-child { border-bottom: 0; }
.cover .meta-card .row.span { padding-top: 3mm; padding-bottom: 3mm; }
.cover .meta-card .label {
  font-size: 8pt;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--light);
  font-weight: 600;
  padding-top: 1px;
}
.cover .meta-card .value {
  color: var(--ink);
  font-weight: 600;
}
.cover .meta-card .value .soft { color: var(--muted); font-weight: 400; }
.cover .meta-card .value .mono { font-family: 'Satoshi', sans-serif; font-weight: 600; letter-spacing: 0.01em; }
.cover .meta-card .value .submitter-name { font-family: 'Satoshi', sans-serif; font-weight: 700; color: var(--ink); }
.cover .meta-card .value .submitter-contact { display: block; font-size: 8.5pt; font-weight: 400; color: var(--muted); margin-top: 1mm; letter-spacing: 0; }
.cover .meta-card .value .locked-pill {
  display: inline-block;
  font-size: 8pt;
  letter-spacing: 0.06em;
  padding: 1.5mm 2.5mm;
  border-radius: 2px;
  background: #ECFDF5;
  color: #047857;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
}

/* ── Scope narrative ─────────────────────────────────────── */
.scope-body {
  white-space: pre-wrap;
  font-size: 10.5pt;
  line-height: 1.7;
  color: var(--ink);
  max-width: 165mm;
}
/* Rich-text scope narrative (TipTap HTML output) — 2026-02.
   Overrides "white-space: pre-wrap" because HTML paragraph tags
   carry their own spacing and pre-wrap would cause a double line-
   break rendering. Legacy plain-text narratives use ".scope-body"
   without the "-rich" modifier and keep pre-wrap. */
.scope-body-rich {
  white-space: normal;
}
.scope-body-rich p {
  margin: 0 0 8pt 0;
}
.scope-body-rich p:last-child { margin-bottom: 0; }
.scope-body-rich h1,
.scope-body-rich h2,
.scope-body-rich h3 {
  font-family: 'Playfair Display', 'Georgia', serif;
  color: var(--ink);
  font-weight: 600;
  margin: 12pt 0 6pt 0;
  line-height: 1.3;
}
.scope-body-rich h1 { font-size: 14pt; }
.scope-body-rich h2 { font-size: 12.5pt; }
.scope-body-rich h3 { font-size: 11.5pt; }
.scope-body-rich strong { font-weight: 700; color: var(--ink); }
.scope-body-rich em { font-style: italic; }
.scope-body-rich u { text-decoration: underline; }
.scope-body-rich s { text-decoration: line-through; color: var(--muted); }
.scope-body-rich ul,
.scope-body-rich ol {
  margin: 4pt 0 8pt 0;
  padding-left: 20pt;
}
.scope-body-rich ul { list-style: disc; }
.scope-body-rich ol { list-style: decimal; }
.scope-body-rich li { margin: 2pt 0; line-height: 1.6; }
.scope-body-rich li > p { margin: 0; }
.scope-body-rich blockquote {
  margin: 6pt 0 6pt 12pt;
  padding-left: 10pt;
  border-left: 2pt solid var(--rule);
  color: var(--muted);
  font-style: italic;
}
.scope-body-rich a {
  color: #047857;
  text-decoration: underline;
}
.scope-body-rich hr {
  border: 0;
  border-top: 1pt solid var(--rule);
  margin: 8pt 0;
}
.scope-body-rich code {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 9.5pt;
  background: #F1F5F9;
  padding: 1pt 4pt;
  border-radius: 3pt;
}
.scope-body-rich pre {
  background: #F1F5F9;
  padding: 8pt 10pt;
  border-radius: 4pt;
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 9.5pt;
  line-height: 1.5;
  overflow-x: auto;
}

/* ── Commercials ─────────────────────────────────────────── */
.commercials-card {
  border: 1px solid var(--rule);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}
.commercials {
  width: 100%;
  border-collapse: collapse;
  font-size: 9.5pt;
}
.commercials thead th {
  background: var(--table-header);
  color: var(--ink);
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 8.5pt;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 3.5mm 4mm;
  border-bottom: 1px solid var(--rule);
}
.commercials thead th.text-left { text-align: left; }
.commercials thead th.text-right { text-align: right; }
.commercials tbody td {
  padding: 3mm 4mm;
  border-bottom: 1px solid #F3F4F6;
  vertical-align: top;
}
.commercials tbody tr:last-child td { border-bottom: 0; }
.commercials .num-col { width: 12mm; color: var(--light); font-feature-settings: "tnum"; }
.commercials .product-name { font-weight: 600; color: var(--ink); font-size: 10pt; }
.commercials .product-sub { font-size: 8.5pt; color: var(--light); margin-top: 1px; letter-spacing: 0.02em; }
.commercials .scope-block { margin-top: 6px; padding-left: 0; }
.commercials .scope-label { font-size: 7.5pt; text-transform: uppercase; letter-spacing: 0.08em; color: var(--light); font-weight: 700; margin-bottom: 2px; }
.commercials .scope-list { margin: 0; padding-left: 14px; list-style: disc; }
.commercials .scope-list li { font-size: 9.5pt; color: var(--ink); line-height: 1.45; margin: 0; }
.commercials .scope-module { font-weight: 500; }
.commercials .scope-channels { color: var(--light); font-size: 8.5pt; }

/* Iter 97.13 — Itemized mode: per-module rows under each product header.
   The product header row carries name+description but blank prices ("—");
   the indented module rows ("↳ Module name") carry the individual setup
   fee and AMC. Visually consistent with the existing scope-list style
   so operators recognise the breakdown immediately. */
.commercials .itemized-product-header td.figure { color: var(--light); font-weight: 400; }
.commercials .itemized-module-row td { border-top: none; padding-top: 2px; padding-bottom: 2px; }
.commercials .itemized-module-row .scope-module-line { font-size: 9.5pt; color: var(--ink); line-height: 1.5; padding-left: 8px; }


/* Iter C.2.1.CAP — capability pills in print. Print-safe text-only pills:
   no icon fonts, no background images, monochrome ink — survive any
   headless-Chromium rendering quirk. */
.commercials .capability-strip { margin-top: 4px; display: flex; flex-wrap: wrap; gap: 4px; }
.commercials .capability-pill {
    display: inline-block;
    font-size: 7.5pt;
    line-height: 1.2;
    padding: 1.5px 5px;
    color: var(--ink);
    background: var(--paper-2, #f4f4f1);
    border: 0.5pt solid var(--rule, #d6d6cf);
    border-radius: 8px;
    letter-spacing: 0.02em;
}

/* Iter 97.8 — Compact delivery-mode icon strip rendered immediately below
   the product description. Restrained monochrome ink to keep the
   commercials card calm. Print-safe (vector SVG icons, no fonts). */
.commercials .capability-icons {
    margin-top: 5px;
    display: inline-flex;
    flex-wrap: wrap;
    gap: 7px;
    align-items: center;
    color: var(--light, #6a6a64);
}
.commercials .capability-icons svg {
    width: 13px;
    height: 13px;
    vertical-align: middle;
}

/* Iter 97.8 — Commercials legend (renders once at the bottom of the
   commercials section, explaining the delivery-mode icon vocabulary). */
.commercials-legend {
    margin-top: 10mm;
    padding: 4mm 5mm;
    border: 0.5pt solid var(--rule, #d6d6cf);
    border-radius: 3px;
    background: var(--paper-2, #f8f8f4);
    font-size: 8pt;
    color: var(--light, #6a6a64);
    line-height: 1.4;
    break-inside: avoid;
    page-break-inside: avoid;
}
.commercials-legend-title {
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-weight: 600;
    color: var(--ink, #1c1c1a);
    font-size: 7.5pt;
    margin-bottom: 3mm;
}
.commercials-legend-items {
    display: flex;
    flex-wrap: wrap;
    gap: 5mm 8mm;
}
.commercials-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--ink, #1c1c1a);
}
.commercials-legend-item svg {
    width: 14px;
    height: 14px;
    color: var(--ink, #1c1c1a);
    flex: 0 0 14px;
}
.commercials .figure {
  text-align: right;
  font-feature-settings: "tnum";
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  color: var(--ink);
  white-space: nowrap;
}
.commercials .figure-em { font-weight: 700; }
.commercials .totals-row td {
  background: var(--card-bg);
  border-top: 2px solid var(--ink);
  border-bottom: 0;
  padding: 4.5mm 4mm;
  font-weight: 700;
  font-size: 11pt;
}
.commercials .totals-row .label {
  text-align: right;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-size: 9pt;
  color: var(--ink);
}
.commercials .option-band td {
  background: var(--reedos-blue);
  color: #fff;
  font-family: 'Satoshi', sans-serif;
  font-weight: 600;
  font-size: 9.5pt;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 3mm 4mm;
}
.commercials .option-subtotal td {
  background: #F1F5F9;
  font-weight: 600;
  font-size: 9pt;
  border-bottom: 1px solid var(--rule);
}
/* Iter 66.14 — Paragraph-length commercial context rendered ONCE under the
   option band. Italic, normal case, dense leading. */
.commercials .option-band-desc td.option-description {
  background: #FAFAFA;
  color: #475569;
  font-size: 8.5pt;
  font-style: italic;
  line-height: 1.45;
  padding: 2.5mm 4mm;
  white-space: pre-wrap;
  border-bottom: 1px solid var(--rule);
  text-transform: none;
  letter-spacing: 0;
}
.commercials .grand-total-row td {
  background: var(--ink);
  color: #fff;
  border-top: 4px double rgba(255,255,255,0.4);
  border-bottom: 0;
  padding: 4mm 4mm;
  font-weight: 700;
  font-size: 10.5pt;
}
.commercials .grand-total-row .label {
  text-align: right;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 9pt;
}
.commercials-footnote {
  font-size: 8.5pt;
  color: var(--light);
  margin-top: 3mm;
  letter-spacing: 0.02em;
}

/* ── C.2.1.e Special Offer commercial-summary block ──────────
   Renders the three-row Standard / Less / Net narrative + GST on Net
   + Grand Total. Replaces the legacy single-total footer only when
   an offer is applied. The internal reason_code is NEVER rendered. */
.commercial-summary {
  margin-top: 6mm;
  padding: 4mm 5mm 4.5mm;
  border: 0.4mm solid var(--ink);
  border-radius: 1.6mm;
  background: #fafafa;
}
.commercial-summary .cs-eyebrow {
  font-size: 8.5pt;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink);
  margin-bottom: 2.5mm;
  border-bottom: 0.25mm solid var(--ink);
  padding-bottom: 1.5mm;
}
.commercial-summary .cs-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.6mm 0;
  font-size: 10pt;
  color: var(--ink);
}
.commercial-summary .cs-row.cs-less {
  color: #8a1d2a;
}
.commercial-summary .cs-row.cs-net {
  border-top: 0.3mm solid #444;
  margin-top: 1.5mm;
  padding-top: 2mm;
  font-weight: 700;
  font-size: 10.5pt;
}
.commercial-summary .cs-row.cs-tax {
  color: var(--light);
  padding-top: 1.5mm;
}
.commercial-summary .cs-row.cs-grand {
  border-top: 0.5mm solid var(--ink);
  margin-top: 2mm;
  padding-top: 2.5mm;
  font-weight: 700;
  font-size: 12pt;
  letter-spacing: 0.04em;
}
.commercial-summary .cs-row .cs-pct {
  color: var(--light);
  font-size: 8.5pt;
  margin-left: 2mm;
}
.commercial-summary .cs-footnote {
  margin-top: 3mm;
  padding-top: 2mm;
  border-top: 0.2mm dashed #b6b6b6;
  font-size: 8.5pt;
  color: var(--light);
  letter-spacing: 0.04em;
  font-style: italic;
}
/* UAT 45.6.6 (2026-07-14) — customer-facing offer description block
   rendered directly under the Less row when the standardized offer
   taxonomy is used. Muted grey, italic, indented so it visually pairs
   with the label above. */
.commercial-summary .cs-description {
  margin-top: 1mm;
  padding-left: 2mm;
  font-size: 8.5pt;
  color: var(--light);
  font-style: italic;
  line-height: 1.35;
}

/* ── Payment terms & T&C body ────────────────────────────── */
.terms-list {
  list-style: decimal;
  padding-left: 6mm;
  font-size: 9.5pt;
  line-height: 1.7;
  color: var(--ink);
}
.terms-list li { padding-left: 1mm; margin-bottom: 1.5mm; }
.terms-list li::marker { color: var(--reedos-blue); font-weight: 700; }

/* 2026-10-23 — keep Payment Terms together on one page when they fit
   (browser still breaks gracefully if a list ever exceeds a full page). */
.keep-together { break-inside: avoid; page-break-inside: avoid; }

.tnc-preamble {
  border-left: 2px solid var(--reedos-orange);
  background: #FFF7F4;
  padding: 4mm 5mm;
  font-size: 9.5pt;
  line-height: 1.7;
  color: var(--ink);
  margin-bottom: 6mm;
  white-space: pre-wrap;
}
.tnc-clauses .clause { margin-bottom: 5.5mm; padding-bottom: 5.5mm; border-bottom: 1px dashed #F1F5F9; }
.tnc-clauses .clause:last-child { border-bottom: 0; padding-bottom: 0; }
/* 2026-10-23 — headings kept with their body across page breaks */
.tnc-clauses .clause-title {
  font-family: 'Satoshi', sans-serif;
  font-weight: 700;
  font-size: 10.5pt;
  color: var(--ink);
  letter-spacing: -0.005em;
  break-after: avoid;
  page-break-after: avoid;
}
.tnc-clauses .clause-title .num { color: var(--reedos-blue); margin-right: 6px; }
/* 2026-10-23 — body renders as per-line blocks (authored line breaks kept
   exactly). Sub-clause numbers (2.1, 15.7 …) get a hanging indent so wrapped
   text aligns under the first word; orphans/widows keep paragraphs tidy
   across pages. */
.tnc-clauses .clause-body {
  font-size: 9.5pt;
  line-height: 1.75;
  color: var(--muted);
  margin-top: 1.5mm;
  orphans: 3;
  widows: 3;
}
.tnc-clauses .clause-body .clause-line,
.tnc-clauses .clause-body .clause-subline,
.tnc-clauses .clause-body .clause-contact-head { white-space: pre-wrap; }
.tnc-clauses .clause-body .clause-subline { padding-left: 8mm; text-indent: -8mm; }
.tnc-clauses .clause-body .clause-contact-head { font-weight: 700; color: var(--ink); }
/* Clause-14 style contact pair ("For the Company" / "For REEDOS") — neat
   two-column arrangement, kept on one page. Wording is untouched; only the
   authored line groups are placed side by side. */
.tnc-contact-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5mm;
  margin-top: 2.5mm;
  break-inside: avoid;
  page-break-inside: avoid;
}
.tnc-contact-col {
  border: 1px solid #E2E8F0;
  border-radius: 2mm;
  background: #FBFDFF;
  padding: 3.5mm 4.5mm;
}

/* ── PB-004 (V1-4.T&C) — Reference / Reference + Additional modes ───── */
/* Reference statement paragraph: shown as a subtle callout so it reads as
   contractually significant text but doesn't shout like a full section. */
.tnc-reference-statement {
  border-left: 2px solid var(--reedos-blue);
  background: #F4F8FF;
  padding: 4mm 5mm;
  font-size: 10pt;
  line-height: 1.75;
  color: var(--ink);
  margin-bottom: 6mm;
  white-space: pre-wrap;
  break-inside: avoid;
  page-break-inside: avoid;
}
/* "Additional Clauses" subheading — visually distinct from body prose so
   the client can tell where operator deltas begin. Restart numbering at 1. */
.tnc-subheading {
  font-family: 'Satoshi', sans-serif;
  font-weight: 700;
  font-size: 11pt;
  color: var(--reedos-blue);
  letter-spacing: -0.005em;
  text-transform: uppercase;
  margin: 6mm 0 3mm 0;
  padding-bottom: 1.5mm;
  border-bottom: 1px solid #E2E8F0;
  break-after: avoid;
  page-break-after: avoid;
}
.tnc-additional-clauses-block { margin-top: 2mm; }

/* 2026-10-23 — closing unit (signatures + document footer) never splits:
   prevents the footer from spilling alone onto an otherwise empty final
   page. If it cannot fit after the last clause, the whole unit moves to a
   proper closing page. */
.doc-end { break-inside: avoid; page-break-inside: avoid; }

/* ── Signatures ──────────────────────────────────────────── */
.signature-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8mm;
  margin-top: 14mm;
}
.signature-block {
  border: 1px solid var(--rule);
  border-top: 3px solid var(--reedos-blue);
  border-radius: 4px;
  padding: 5mm 5mm 6mm 5mm;
  min-height: 48mm;
  display: flex;
  flex-direction: column;
}
.signature-block .for-label {
  font-size: 7.5pt;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--light);
  margin-bottom: 1.5mm;
}
.signature-block .for-name {
  font-family: 'Satoshi', sans-serif;
  font-weight: 700;
  font-size: 11pt;
  color: var(--ink);
  letter-spacing: -0.005em;
  line-height: 1.25;
  min-height: 16pt;
}
.signature-block .sign-line {
  margin-top: auto;
  border-bottom: 1px solid var(--rule-strong);
  padding-bottom: 1mm;
  height: 18mm;
}
.signature-block .sign-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4mm;
  margin-top: 3mm;
  font-size: 8pt;
  color: var(--light);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 600;
}

/* ── Document footer (printed once at the end) ───────────── */
.doc-footer {
  margin-top: 14mm;
  padding-top: 4mm;
  border-top: 1px solid var(--rule);
  font-size: 8pt;
  color: var(--light);
  letter-spacing: 0.02em;
}
.doc-footer .confidentiality-block {
  margin-bottom: 4mm;
  padding-bottom: 3.5mm;
  border-bottom: 1px solid var(--rule);
  color: var(--muted);
  font-size: 7.5pt;
  line-height: 1.55;
  letter-spacing: 0.01em;
}
.doc-footer .confidentiality-block .conf-eyebrow {
  display: block;
  font-size: 7pt;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--reedos-orange);
  margin-bottom: 1.5mm;
}
.doc-footer .footer-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.doc-footer .org-line strong { color: var(--ink); font-weight: 700; }
.doc-footer .conf {
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 600;
}

/* ── Cover-page confidentiality notice (small, subtle) ─────── */
.cover .cover-confidentiality {
  margin-top: 8mm;
  padding-top: 4mm;
  border-top: 1px solid var(--rule);
  font-size: 7pt;
  line-height: 1.55;
  letter-spacing: 0.01em;
  color: var(--muted);
  max-width: 165mm;
}
.cover .cover-confidentiality .conf-eyebrow {
  display: block;
  font-size: 6.5pt;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--reedos-orange);
  margin-bottom: 1.5mm;
}
`;
